/**
 * CERTIFICATE OF INSURANCE CARD (owner, 28 Oct 2026: "drivers need to be able to upload coi if a
 * job their on requires it and were getting theirs instead of using our own").
 *
 * Some apartment buildings only let movers in with a certificate of insurance (COI) naming the
 * building. A driver asked whether he had one replied "Yes for tomorrow. How do you want it?" and
 * the app had nowhere to put it. This card lists each building on the job that needs one: what
 * the building sent (its sample COI or instructions, the name the COI must list), where the COI
 * stands, and an Upload button for the driver's own COI (a PDF or a photo). Ops checks the upload
 * and sends it to the customer from dispatch; nothing is sent to anyone from here.
 *
 * The job payload carries a compact `coi` ({ needed, parties }) so this renders nothing, with no
 * extra request, on the many jobs that need no COI. Before the driver has the job (job board
 * detail) it is a one-line heads-up; uploading needs the job to be theirs (GET /jobs/:id/coi 403s).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, Platform, Linking, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { api } from '../services/api';
import { RD } from '../theme/colors';

type CoiFile = { url: string; name: string; type: string };
type Party = 'seller' | 'buyer';
type CoiParty = {
  party: Party;
  label: string;
  note: string | null;
  files: CoiFile[];
  driverCoi: (CoiFile & { driverName: string | null; uploadedAt: string }) | null;
  completed: CoiFile | null;
  completedBy: 'ops' | 'driver' | null;
  sentAt: string | null;
};
export type CoiSummary = { needed: boolean; parties: { party: Party; uploaded: boolean }[] } | null | undefined;

const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const building = (p: Party) => (p === 'seller' ? 'pickup building' : 'delivery building');

function openLink(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') { window.open(url, '_blank', 'noopener'); return; }
  Linking.openURL(url).catch(() => {});
}
function notify(title: string, msg: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') window.alert(`${title}\n\n${msg}`);
  else Alert.alert(title, msg);
}

/** A PDF or a photo. On web (the driver app is a PWA) a plain file input: it offers Files and the camera roll. */
function pickCoiFile(): Promise<{ web?: File; uri?: string; name: string; type: string } | null> {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/pdf,image/*';
      input.onchange = () => {
        const f = input.files?.[0];
        resolve(f ? { web: f, name: f.name || 'COI', type: f.type || '' } : null);
      };
      input.click();
    });
  }
  return ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.85 })
    .then((r) => (!r.canceled && r.assets?.[0] ? { uri: r.assets[0].uri, name: r.assets[0].fileName || 'coi.jpg', type: r.assets[0].mimeType || 'image/jpeg' } : null))
    .catch(() => null);
}

export function CoiCard({ jobId, summary, canUpload }: { jobId: string; summary: CoiSummary; canUpload: boolean }) {
  const [parties, setParties] = useState<CoiParty[] | null>(null);
  const [busy, setBusy] = useState<Party | null>(null);
  const [err, setErr] = useState('');
  const needed = !!summary?.needed;

  const load = useCallback(() => {
    if (!needed || !canUpload || !jobId) return;
    api.get(`/jobs/${jobId}/coi`).then(({ data }) => setParties(data?.parties || [])).catch(() => setParties(null));
  }, [jobId, needed, canUpload]);
  useEffect(() => { load(); }, [load]);

  if (!needed) return null;

  const upload = async (party: Party) => {
    setErr('');
    const f = await pickCoiFile();
    if (!f) return;
    if (!/pdf|image/i.test(f.type) && !/\.(pdf|jpe?g|png|heic|heif|webp)$/i.test(f.name)) { setErr('Upload your COI as a PDF or a photo.'); return; }
    if (f.web && f.web.size > 15 * 1024 * 1024) { setErr('That file is over 15 MB. Send a smaller PDF or a photo.'); return; }
    setBusy(party);
    try {
      const fd = new FormData();
      if (f.web) fd.append('file', f.web, f.name);
      else fd.append('file', { uri: f.uri, name: f.name, type: f.type } as any);
      // On web the browser must set the multipart boundary itself (see LoadedPhotoScreen).
      const { data } = await api.post(`/jobs/${jobId}/coi/${party}`, fd, {
        headers: Platform.OS === 'web' ? undefined : { 'Content-Type': 'multipart/form-data' },
        timeout: 75000,
      });
      setParties(data?.parties || []);
      notify('COI uploaded', `Thanks. Ops will check it and send it to the ${party === 'seller' ? 'seller' : 'buyer'} for the building.`);
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'The upload did not go through. Check your connection and try again.');
    } finally { setBusy(null); }
  };

  const card = { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.amber + '55', padding: 16, marginBottom: 12, gap: 10 } as const;
  const header = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Ionicons name="shield-checkmark-outline" size={18} color={RD.amber} />
      <Text style={{ fontSize: 16, fontWeight: '700', color: RD.text }}>Certificate of insurance</Text>
    </View>
  );

  // Not this driver's job yet (job board), or the full view could not load: the heads-up only.
  if (!canUpload || !parties) {
    const which = (summary?.parties || []).map((p) => building(p.party));
    return (
      <View style={card}>
        {header}
        <Text style={{ fontSize: 13.5, color: RD.textSec, lineHeight: 19 }}>
          The {which.join(' and the ') || 'building'} needs a certificate of insurance (COI) naming the building.
          {canUpload ? '' : ' If you have your own, you can upload it on the job after you accept.'}
        </Text>
        {canUpload ? (
          <TouchableOpacity onPress={load} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="refresh" size={15} color={RD.info} />
            <Text style={{ fontSize: 13.5, color: RD.info, fontWeight: '700' }}>Load the COI details to upload yours</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={card}>
      {header}
      <Text style={{ fontSize: 13, color: RD.textSec, lineHeight: 19 }}>
        This building only lets movers in with a COI. If you have your own insurance, upload your COI here. Ops checks it and sends it to the customer for the building.
      </Text>
      {parties.map((p, i) => {
        const mine = p.driverCoi && p.completedBy === 'driver';
        const status = mine
          ? (p.sentAt ? `Sent to the customer ${fmt(p.sentAt)}` : `Uploaded ${fmt(p.driverCoi!.uploadedAt)}. Ops is checking it.`)
          : p.completed
            ? (p.sentAt ? `Ours was sent to the customer ${fmt(p.sentAt)}. You can still upload yours.` : 'Ours is on file. You can still upload yours.')
            : 'Not uploaded';
        const statusColor = mine && p.sentAt ? RD.money : mine ? RD.info : p.completed ? RD.textSec : RD.amber;
        return (
          <View key={p.party} style={{ borderTopWidth: i === 0 ? 0 : 1, borderTopColor: RD.g05, paddingTop: i === 0 ? 0 : 10, gap: 6 }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: RD.text }}>{p.label}</Text>
            {p.note ? (
              <Text style={{ fontSize: 13, color: RD.textSec, lineHeight: 19 }}>
                <Text style={{ fontWeight: '700' }}>The COI must name: </Text>{p.note}
              </Text>
            ) : null}
            {p.files.length > 0 ? (
              <View style={{ gap: 4 }}>
                <Text style={{ fontSize: 12, fontWeight: '700', color: RD.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>What the building sent</Text>
                {p.files.map((f) => (
                  <TouchableOpacity key={f.url} onPress={() => openLink(f.url)} activeOpacity={0.7} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Ionicons name={/pdf/i.test(f.type) ? 'document-text-outline' : 'image-outline'} size={15} color={RD.info} />
                    <Text style={{ fontSize: 13.5, color: RD.info, fontWeight: '600', flexShrink: 1 }} numberOfLines={1}>{f.name}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              !p.note && <Text style={{ fontSize: 13, color: RD.textFaint }}>The building has not sent a sample yet. Ops will message you what it must say.</Text>
            )}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Ionicons name={mine ? 'checkmark-circle' : 'ellipse-outline'} size={15} color={statusColor} />
              <Text style={{ fontSize: 13, color: statusColor, fontWeight: '600', flexShrink: 1 }}>{status}</Text>
            </View>
            {mine ? (
              <TouchableOpacity onPress={() => openLink(p.driverCoi!.url)} activeOpacity={0.7}>
                <Text style={{ fontSize: 13, color: RD.info, fontWeight: '600' }} numberOfLines={1}>View your COI: {p.driverCoi!.name}</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              onPress={() => upload(p.party)}
              disabled={!!busy}
              activeOpacity={0.85}
              style={{ marginTop: 2, backgroundColor: mine ? RD.surface : RD.ink, borderWidth: mine ? 1.5 : 0, borderColor: RD.borderStrong, borderRadius: 12, height: 44, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: busy && busy !== p.party ? 0.5 : 1 }}
            >
              {busy === p.party
                ? <ActivityIndicator color={mine ? RD.text : '#fff'} />
                : <>
                    <Ionicons name="cloud-upload-outline" size={17} color={mine ? RD.text : '#fff'} />
                    <Text style={{ fontSize: 14, fontWeight: '700', color: mine ? RD.text : '#fff' }}>{mine ? 'Replace your COI' : 'Upload your COI'}</Text>
                  </>}
            </TouchableOpacity>
          </View>
        );
      })}
      {err ? <Text style={{ fontSize: 12.5, color: RD.danger }}>{err}</Text> : null}
      <Text style={{ fontSize: 11.5, color: RD.textFaint }}>A PDF or a photo of your COI. No COI? Message us from the job and we will get one.</Text>
    </View>
  );
}
